import { ContentStack, Content, ContentTable, PageBreak } from "pdfmake/interfaces";
import {
  Allergies,
  CheckupReportCover,
  CheckupReportFooter,
  CheckupReportLab,
  CheckupReportXray,
  Cover,
  HealthBehaviors,
  ImagingOrderItem,
  LabDetailItem,
  LabDivision,
  LabDivisionItem,
  PhysicalExamination,
  VitalSigns,
} from "./CheckupReportType";
import { HealthRecommendationData } from "../sequence/HealthRecommendation";

// Commons
import {
  DEFAULT_FONT_SIZES,
  getCheckupReportFooter,
  getCheckupReportHeader,
  ITDocumentDefinitions,
} from "./CheckupReportCommon";

// Utils
import moment from "moment";
import { formatISOtoBEDateTime, formatADDateToThaiTextDate } from "react-lib/utils/dateUtils";

// Types
export type FormCheckupReportProps = {
  coverData: CheckupReportCover;
  labData: CheckupReportLab;
  xrayData: CheckupReportXray;
  data: HealthRecommendationData | undefined;
  language: "th" | "en";
  printer: string;
};

export type FormCheckupReportDoctorNote = {
  doctorNoteArray: Content[];
  doctor?: string;
  doctor_number?: string;
};

const COVER_PATIENT_INFO_FIELDS: {
  labelTh: string;
  labelEn: string;
  valueKey: keyof Cover;
  formatterTh?: (v: any) => void;
  formatterEn?: (v: any) => void;
}[] = [
  { labelTh: "HN", labelEn: "HN", valueKey: "patient_hn" },
  { labelTh: "เพศ", labelEn: "Gender", valueKey: "sex" },
  {
    labelTh: "วันเดือนปีเกิด",
    labelEn: "Birthdate",
    valueKey: "birthdate",
    formatterTh: formatADDateToThaiTextDate,
    formatterEn: (v) => moment(v).format("D MMMM YYYY"),
  },
  { labelTh: "อายุ", labelEn: "Age", valueKey: "age" },
  { labelTh: "หมายเลขรับบริการ", labelEn: "Visit Number", valueKey: "visit_number" },
  {
    labelTh: "วันที่รับบริการ",
    labelEn: "Visit Date",
    valueKey: "visit_date",
    formatterTh: (v) => formatISOtoBEDateTime(v).dateTimeShortBracket,
    formatterEn: (v) => `${moment(v).format("DD/MM/YYYY")} [${moment(v).format("HH:mm")}]`
  },
];

/*================================ GENERATE FUNCS ==============================*/

const buildCoverPackageTable = (packageName: string, language: "th" | "en"): ContentTable => {
  const isEng = language === "en";

  return {
    table: {
      widths: ["*", "70%", "*"],
      body: [
        [
          {
            text: ``,
            border: [false],
          },
          {
            text: [
              { text: isEng ? `Package: ` : `แพ็กเกจ: `, bold: true },
              { text: packageName || "" },
            ],
            style: "cover_package",
          },
          {
            text: ``,
            border: [false],
          },
        ],
      ],
    },
    layout: {
      hLineWidth: (i, node) => {
        return i === 0 || i === node.table.body.length ? 1 : 0;
      },
      hLineStyle: (i, node) => {
        if (i === 0 || i === node.table.body.length) {
          return { dash: { length: 4, space: 2 } };
        }
        return null;
      },
      vLineWidth: () => 0,
      vLineStyle: () => null,
    },
    marginBottom: 30,
  };
};

const buildCoverPatientInfoTable = (coverData: Cover, language: "th" | "en"): ContentTable => {
  const isEng = language === "en";

  return {
    table: {
      widths: ["*", "30%", "40%", "*"],
      body: [
        [
          { text: ``, border: [false] },
          {
            text: [{ text: `HN: `, bold: true }, { text: coverData.patient_hn || "" }],
            colSpan: 2,
          },
          {},
          { text: ``, border: [false] },
        ],
        [
          { text: ``, border: [false] },
          {
            text: coverData.patient_name || "",
            colSpan: 2,
          },
          {},
          { text: ``, border: [false] },
        ],
        ...COVER_PATIENT_INFO_FIELDS.slice(1).map((field) => [
          { text: ``, border: [false] },
          { text: isEng ? field.labelEn : field.labelTh, bold: true },
          {
            text: isEng
              ? field.formatterEn?.(coverData[field.valueKey]) || coverData[field.valueKey] || ""
              : field.formatterTh?.(coverData[field.valueKey]) || coverData[field.valueKey] || "",
          },
          { text: ``, border: [false] },
        ]),
      ],
    },
    layout: "noBorders",
  };
};

const buildExamVitalSignsTable = (vitalSignsData: VitalSigns): ContentTable => ({
  table: {
    widths: ["40%", "20%", "40%"],
    headerRows: 1,
    body: [
      [
        {
          text: "สัญญาณชีพ/Vital Signs",
          bold: true,
        },
        {
          text: formatISOtoBEDateTime(vitalSignsData.measurement_datetime).dateTimeShortBracket,
          bold: true,
          colSpan: 2,
        },
        {},
      ],
      ...vitalSignsData.items.map((item) => [
        { text: item.label },
        { text: item.value },
        { text: item.unit },
      ]),
    ],
  },
  layout: "noBorders",
  marginBottom: 10,
});

const buildExamAllergiesTable = (allergiesData: Allergies): ContentTable => ({
  table: {
    widths: ["40%", "60%"],
    headerRows: 1,
    body: [
      [
        {
          text: "ประวัติการแพ้/History of Allergies",
          bold: true,
          colSpan: 2,
        },
        {},
      ],
      ...allergiesData.items.map((item) => [{ text: item.label }, { text: item.name }]),
    ],
  },
  layout: "noBorders",
  marginBottom: 10,
});

const buildExamHealthBehaviorsTable = (healthBehaviorsData: HealthBehaviors): ContentTable => ({
  table: {
    widths: ["40%", "60%"],
    headerRows: 1,
    body: [
      [
        {
          text: "พฤติกรรมทางสุขภาพ/Health Behavior",
          bold: true,
          colSpan: 2,
        },
        {},
      ],
      ...healthBehaviorsData.items.map((item) => [{ text: item.label }, { text: item.behavior }]),
    ],
  },
  layout: "noBorders",
});

const buildLabDivisionTable = (division: LabDivision): ContentStack => ({
  // stack: [
  //   {
  //     text: division.lab_division_name,
  //     style: "page_title",
  //     marginBottom: 10,
  //   },
  //   ...division.items.map((lab: LabDivisionItem) => buildLabDetailItemTable(lab)),
  // ],

  stack: [
    {
      table: {
        widths: ["55%", "10%", "20%", "15%"],
        headerRows: 1,
        body: [
          [
            {
              text: division.lab_division_name,
              style: "page_title",
              marginBottom: 5,
            },
            {
              text: "Unit",
              bold: true,
            },
            {
              text: "Ref.value",
              bold: true,
            },
            ...Array(1).fill({}),
          ],
          [
            {
              stack: [
                ...division.items.map((lab: LabDivisionItem) => buildLabDetailItemTable(lab)),
              ],
              colSpan: 4,
            },
            ...Array(3).fill({}),
          ],
        ],
      },
      layout: "noBorders",
      marginBottom: 10,
    },
  ],
});

const buildLabDetailItemTable = (lab: LabDivisionItem): ContentTable => ({
  table: {
    widths: ["40%", "15%", "10%", "20%", "15%"],
    headerRows: 1,
    body: [
      [
        {
          text: lab.name,
          bold: true,
          colSpan: 5,
        },
        ...Array(4).fill({}),
      ],
      ...lab.items.flatMap((item: LabDetailItem) => {
        const mainRow = [
          { text: item.name || "" },
          { text: item.value || "" },
          { text: item.unit || "" },
          { text: `(${item.ref_value.replace(";", "\n")})` || "" },
          { text: item.status || "", alignment: "center" },
        ];

        // --- Recommendation
        if (item.recommendation) {
          const recommendationRow = [
            {},
            {
              text: item.recommendation,
              italics: true,
              colSpan: 4,
            },
            ...Array(3).fill({}),
          ];

          return [mainRow, recommendationRow];
        }

        return [mainRow];
      }),
    ],
  },
  layout: "noBorders",
  marginBottom: 10,
});

const buildXrayOrderTable = (item: ImagingOrderItem, language: "th" | "en"): ContentTable => {
  const isEng = language === "en";

  return {
    table: {
      widths: ["50%", "50%"],
      headerRows: 3,
      body: [
        [
          {
            text: item.name,
            bold: true,
          },
          {
            text: [{ text: "Accession No. " }, { text: item.accession_id || "-" }],
            bold: true,
          },
        ],
        [
          {
            text: [
              {
                text: isEng ? "Order date " : "วันที่สั่งตรวจ ",
                bold: true,
              },
              { text: formatISOtoBEDateTime(item.executed_datetime).dateTimeShortBracket },
            ],
          },
          {
            text: [
              {
                text: isEng ? "Requested by " : "ผู้ส่งตรวจ ",
                bold: true,
              },
              { text: item.executed_by || "" },
            ],
          },
        ],
        [
          {
            text: [
              {
                text: isEng ? "Report date " : "วันที่รายงานผล ",
                bold: true,
              },
              { text: formatISOtoBEDateTime(item.reported_datetime).dateTimeShortBracket },
            ],
            marginBottom: 10,
          },
          {
            text: [
              {
                text: isEng ? "Radiologist " : "รังสีแพทย์ ",
                bold: true,
              },
              { text: item.radiologist || "" },
            ],
            marginBottom: 10,
          },
        ],
        [
          {
            text: "FINDINGS:",
            bold: true,
            colSpan: 2,
          },
          {},
        ],
        [
          {
            text: item.finding || "",
            marginBottom: 10,
            colSpan: 2,
          },
          {},
        ],
        [
          {
            text: "IMPRESSION:",
            bold: true,
            colSpan: 2,
          },
          {},
        ],
        [
          {
            text: item.impression || "",
            colSpan: 2,
          },
          {},
        ],
      ],
    },
    layout: "noBorders",
    marginBottom: 20,
  };
};

/*================================ COVER SECTION ==============================*/

const generateCoverPage = (coverData: Cover, language: "th" | "en"): ContentStack => ({
  stack: [
    {
      image: "logo",
      width: 140,
      style: "cover_logo",
    },
    {
      text: "รายงานผลการตรวจสุขภาพ/ทางการแพทย์",
      style: "cover_title",
    },
    buildCoverPackageTable(coverData.package_name, language),
    buildCoverPatientInfoTable(coverData, language),
  ],
});

/*================================ EXAMINATION SECTION ==============================*/

const generateExaminationPage = (examData: PhysicalExamination): ContentStack => ({
  stack: [
    {
      text: "การตรวจร่างกาย/Physical Examination",
      style: "page_title",
    },
    buildExamVitalSignsTable(examData.vital_signs),
    buildExamAllergiesTable(examData.allergies),
    buildExamHealthBehaviorsTable(examData.health_behaviors),
  ],
});

/*================================ LAB SECTION ==============================*/

const generateLabPage = (labData: CheckupReportLab): ContentStack => ({
  stack: [...labData.lab_divisions.map((division) => buildLabDivisionTable(division))],
});

/*================================ XRAY SECTION ==============================*/

const generateXrayPage = (xrayData: CheckupReportXray, language: "th" | "en"): ContentStack => {
  return {
    stack: [...xrayData.imaging_order_items.map((item) => buildXrayOrderTable(item, language))],
  };
};

/*================================ MAIN ==============================*/

const FormCheckupReport = async (props: FormCheckupReportProps): Promise<ITDocumentDefinitions> => {
  const { coverData, labData, xrayData, printer, language = "th" } = props;
  const currentDate = moment();

  const footerData: CheckupReportFooter = {
    currentDate,
    printer,
  };

  const headerForm = await getCheckupReportHeader({
    detail: coverData.cover,
    language,
  });

  const { font, fontSizes, images, lineHeights, styles } = headerForm;

  return {
    pageSize: "A4",
    defaultStyle: {
      font,
      fontSize: fontSizes[DEFAULT_FONT_SIZES],
      lineHeight: lineHeights[1],
    },
    ...headerForm,
    images: { ...images },
    styles: {
      ...styles,
      cover_title: {
        fontSize: fontSizes[20],
        alignment: "center",
        bold: true,
        marginBottom: 30,
      },
      cover_logo: {
        alignment: "center",
        marginBottom: 30,
      },
      cover_package: {
        alignment: "center",
        margin: [20, 10, 20, 10],
      },
      page_title: {
        fontSize: fontSizes[16],
        bold: true,
        marginBottom: 10,
      },
      footer_title: {
        fontSize: fontSizes[12],
      },
      footer_info: {
        fontSize: fontSizes[11],
      },
    },
    content: [
      // * --- Cover Page
      generateCoverPage(coverData.cover, language),
      { text: ``, pageBreak: "after" },

      // * --- Examination Page
      generateExaminationPage(coverData.physical_examination),
      { text: ``, pageBreak: "after" },

      // * --- Lab Page
      ...(labData.lab_divisions?.length > 0
        ? [generateLabPage(labData), { text: ``, pageBreak: "after" as PageBreak }]
        : []),

      // * --- Xray Page
      ...(xrayData.imaging_order_items?.length > 0 ? [generateXrayPage(xrayData, language)] : []),
    ],
    footer: (currentPage, countPage) =>
      getCheckupReportFooter({
        currentPage,
        countPage,
        footerData,
        language,
      }),
  };
};

export default FormCheckupReport;
